/**
 * Configuración del servidor para el frontend de banking
 * Permite cambiar entre servidor local y producción
 */

export type ServerEnvironment = 'local' | 'production';

export const SERVER_URLS = {
  local: 'http://localhost:5000/api',
  production: 'https://api.v2.belobaba.io/api'
} as const;

export const SERVER_CONFIG = {
  local: {
    base: 'http://localhost:5000',
    api: 'http://localhost:5000/api',
    ws: 'ws://localhost:5000',
    name: 'Local',
    color: 'blue'
  },
  production: {
    base: 'https://api.v2.belobaba.io',
    api: 'https://api.v2.belobaba.io/api',
    ws: 'wss://api.v2.belobaba.io',
    name: 'Producción',
    color: 'green'
  }
} as const;

/**
 * Obtener configuración del servidor según el entorno
 */
export const getServerConfig = (environment?: ServerEnvironment) => {
  if (!environment && typeof window !== 'undefined') {
    const savedEnv = localStorage.getItem('serverEnvironment') as ServerEnvironment;
    environment = (savedEnv && savedEnv in SERVER_CONFIG) ? savedEnv : 'production';
  }
  return SERVER_CONFIG[environment || 'production'];
};

/**
 * Obtener la URL actual de la API
 * - En producción desplegada (banking.belobaba.io, kanzbank.com, CloudFront): siempre usa https://api.v2.belobaba.io/api
 * - En desarrollo local: respeta el switch del usuario (local o production)
 */
export const getCurrentApiUrl = () => {
  // Si estamos en el build de producción desplegado, forzar API de producción
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'banking.belobaba.io' ||
        hostname === 'kanzbank.com' ||
        hostname === 'www.kanzbank.com' ||
        hostname.includes('.cloudfront.net')) {
      return SERVER_CONFIG.production.api;
    }
  }

  // En desarrollo local, respetar el switch de entorno
  if (typeof window !== 'undefined') {
    const savedEnv = localStorage.getItem('serverEnvironment') as ServerEnvironment;
    if (savedEnv === 'local') {
      return SERVER_CONFIG.local.api;
    } else if (savedEnv === 'production') {
      return SERVER_CONFIG.production.api;
    }
    // Por defecto en desarrollo: usar local
    return SERVER_CONFIG.local.api;
  }

  // Fallback: producción
  return SERVER_CONFIG.production.api;
};

/**
 * Obtener la URL del servidor según el entorno actual
 */
export function getServerUrl(includeApi: boolean = true): string {
  const savedEnv = localStorage.getItem('serverEnvironment') as ServerEnvironment;
  const environment = savedEnv || 'production'; // Default a producción

  return includeApi ? SERVER_CONFIG[environment].api : SERVER_CONFIG[environment].base;
}

/**
 * Obtener el entorno actual
 */
export function getCurrentEnvironment(): ServerEnvironment {
  const savedEnv = localStorage.getItem('serverEnvironment') as ServerEnvironment;
  return savedEnv || 'production';
}

/**
 * Establecer el entorno
 */
export function setServerEnvironment(env: ServerEnvironment): void {
  localStorage.setItem('serverEnvironment', env);
}

/**
 * Obtener la URL base actual (sin /api)
 * - En producción desplegada: https://api.v2.belobaba.io
 * - En desarrollo local: respeta el switch del usuario
 */
export const getCurrentBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'banking.belobaba.io' ||
        hostname === 'kanzbank.com' ||
        hostname === 'www.kanzbank.com' ||
        hostname.includes('.cloudfront.net')) {
      return SERVER_CONFIG.production.base;
    }
  }

  if (typeof window !== 'undefined') {
    const savedEnv = localStorage.getItem('serverEnvironment') as ServerEnvironment;
    if (savedEnv === 'local') {
      return SERVER_CONFIG.local.base;
    } else if (savedEnv === 'production') {
      return SERVER_CONFIG.production.base;
    }
    return SERVER_CONFIG.local.base;
  }
  return SERVER_CONFIG.production.base;
};
